import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, UserCheck, CalendarClock } from "lucide-react";
import { DateRange, FilterSelect, FilterShell, presetRange, toApiRange } from "@/components/Filters";
import { toast } from "sonner";
import LeadDrawer from "@/components/LeadDrawer";
import { sourceLabel } from "@/components/StudentProfile";

const STATUS_TONE = { booked: "bg-amber-100 text-amber-800", attended: "bg-emerald-100 text-emerald-800", no_show: "bg-red-100 text-red-800", cancelled: "bg-slate-100 text-slate-600", rescheduled: "bg-blue-100 text-blue-800" };
const OUTCOME_LABEL = { follow_up: "Follow-up needed", admission_done: "Admission done", not_interested: "Not interested" };

export default function Appointments() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lead_name: "", phone: "", scheduled_at: "", kind: "counselling", counsellor_id: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Outcome dialog for Attended / No-show
  const [oc, setOc] = useState(null); // { appt, type: 'attended' | 'no_show' }
  const [ocForm, setOcForm] = useState({ outcome: "", session_notes: "", next_step: "" });

  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [counsellor, setCounsellor] = useState("");
  const [search, setSearch] = useState("");
  const [openLead, setOpenLead] = useState(null);
  const { data: leadConfig } = useQuery({ queryKey: ["leadconfig"], queryFn: async () => (await api.get("/leads/meta/config")).data });
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState(presetRange("all"));
  const apiR = toApiRange(range);
  const apptParams = { q: search || undefined, status: status || undefined, kind: kind || undefined, counsellor_id: counsellor || undefined, start: apiR.date_from, end: apiR.date_to || (apiR.date_from ? new Date().toISOString() : undefined) };
  const { data, isLoading } = useQuery({ queryKey: ["appts", apptParams], queryFn: async () => (await api.get("/appointments", { params: apptParams })).data, refetchInterval: 30000 });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/users")).data, enabled: ["admin", "reception"].includes(user?.role) });
  const clearFilters = () => { setSearch(""); setStatus(""); setKind(""); setCounsellor(""); setPreset("all"); setRange(presetRange("all")); };
  const summary = (data || []).reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {});

  const create = useMutation({
    mutationFn: async () => (await api.post("/appointments", { ...form, counsellor_id: form.counsellor_id || null, scheduled_at: new Date(form.scheduled_at).toISOString() })).data,
    onSuccess: () => { toast.success("Appointment booked"); setOpen(false); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/appointments/${id}`, body)).data,
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openOutcome = (appt, type) => { setOc({ appt, type }); setOcForm({ outcome: "", session_notes: "", next_step: "", reschedule_at: "", followup_at: "" }); };
  const submitOutcome = async () => {
    try {
      if (oc.type === "attended") {
        await api.put(`/appointments/${oc.appt.id}`, { status: "attended", outcome: ocForm.outcome, session_notes: ocForm.session_notes });
        if (ocForm.outcome === "follow_up" && ocForm.followup_at && oc.appt.lead_id) {
          await api.post(`/leads/${oc.appt.lead_id}/activity`, { type: "note", note: "Follow-up call after counselling", next_follow_up_at: new Date(ocForm.followup_at).toISOString() });
        }
      } else {
        // Create the rescheduled appointment FIRST so a slot conflict (409) aborts
        // before the original is marked no-show (keeps the action atomic for the user).
        if (ocForm.reschedule_at) {
          await api.post("/appointments", { lead_id: oc.appt.lead_id || null, lead_name: oc.appt.lead_name, phone: oc.appt.phone, counsellor_id: oc.appt.counsellor_id || null, scheduled_at: new Date(ocForm.reschedule_at).toISOString(), kind: oc.appt.kind || "demo", note: "Rescheduled after no-show" });
        }
        await api.put(`/appointments/${oc.appt.id}`, { status: "no_show", session_notes: ocForm.session_notes, next_step: ocForm.next_step });
        if (ocForm.followup_at && oc.appt.lead_id) {
          await api.post(`/leads/${oc.appt.lead_id}/activity`, { type: "note", note: "Follow-up call after no-show", next_follow_up_at: new Date(ocForm.followup_at).toISOString() });
        }
        if (ocForm.followup_at && !oc.appt.lead_id) toast.message("Follow-up not scheduled — this appointment has no linked lead.");
      }
      toast.success("Saved");
      setOc(null);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title="Appointments" subtitle="Counselling & demo visits — booked vs attended are separate statuses"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-brand-blue hover:bg-blue-700 gap-2" data-testid="add-appt-btn"><Plus className="w-4 h-4" /> Book</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Book appointment</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input data-testid="appt-name" value={form.lead_name} onChange={(e) => set("lead_name", e.target.value)} /></div>
                  <div><Label>Phone</Label><Input data-testid="appt-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                </div>
                <div><Label>Date & time</Label><Input type="datetime-local" data-testid="appt-datetime" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Type</Label>
                    <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                      <SelectTrigger data-testid="appt-kind"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="counselling">Counselling</SelectItem><SelectItem value="demo">Demo</SelectItem><SelectItem value="walk-in">Walk-in</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Counsellor</Label>
                    <Select value={form.counsellor_id || "none"} onValueChange={(v) => set("counsellor_id", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="appt-counsellor"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Any</SelectItem>{(staff || []).filter((s) => s.role === "sales" && s.active !== false).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Note</Label><Textarea data-testid="appt-note" value={form.note} onChange={(e) => set("note", e.target.value)} /></div>
              </div>
              <DialogFooter><Button className="bg-brand-blue hover:bg-blue-700" disabled={!form.lead_name || !form.scheduled_at} onClick={() => create.mutate()} data-testid="appt-save">Book</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } testid="appts-header" />

      <FilterShell onClear={clearFilters} canExport={user?.role === "admin"} exportPath="/csv/export/appointments" exportParams={apptParams}>
        <Input className="w-[220px]" placeholder="Search name or phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="appt-search" />
        <DateRange preset={preset} setPreset={setPreset} range={range} setRange={setRange} />
        <FilterSelect value={status} onChange={setStatus} placeholder="Status" options={[{ value: "booked", label: "Booked" }, { value: "attended", label: "Attended" }, { value: "no_show", label: "No-show" }, { value: "cancelled", label: "Cancelled" }, { value: "rescheduled", label: "Rescheduled" }]} testid="f-appt-status" />
        <FilterSelect value={kind} onChange={setKind} placeholder="Type" options={[{ value: "counselling", label: "Counselling" }, { value: "demo", label: "Demo" }, { value: "walk-in", label: "Walk-in" }]} testid="f-appt-kind" width="w-[130px]" />
        {["admin", "reception"].includes(user?.role) && (
          <FilterSelect value={counsellor} onChange={setCounsellor} placeholder="Counsellor" options={(staff || []).filter((s) => s.role === "sales" && s.active !== false).map((s) => ({ value: s.id, label: s.name }))} testid="f-appt-counsellor" width="w-[160px]" />
        )}
      </FilterShell>

      {data?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="appt-summary">
          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 font-medium">Total: {data.length}</span>
          {[["booked", "Booked", "bg-amber-100 text-amber-800"], ["attended", "Attended", "bg-emerald-100 text-emerald-800"], ["no_show", "No-show", "bg-red-100 text-red-800"], ["cancelled", "Cancelled", "bg-slate-100 text-slate-600"]].map(([k, label, cls]) => (
            <span key={k} className={`text-xs px-2.5 py-1 rounded-full font-medium ${cls}`}>{label}: {summary[k] || 0}</span>
          ))}
        </div>
      )}

      {isLoading ? <Loading /> : !data?.length ? <Empty title="No appointments" subtitle="Book a counselling or demo visit." /> : (
        <div className="space-y-2">
          {data.map((a) => (
            <div key={a.id} className="p-4 rounded-xl border bg-white dark:bg-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`appt-${a.id}`}>
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {a.lead_id ? <button className="text-brand-blue hover:underline text-left" onClick={() => setOpenLead(a.lead_id)} data-testid={`appt-open-${a.id}`}>{a.lead_name}</button> : a.lead_name}
                  <Badge variant="secondary" className="text-[10px]">{a.kind}</Badge>
                  {a.lead_source && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700" data-testid={`appt-source-${a.id}`}>Source: {sourceLabel(a.lead_source)}</span>}
                </div>
                <div className="text-sm text-slate-500 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> {fmtDate(a.scheduled_at)} • {a.phone}</div>
                <div className="text-[11px] text-slate-400">Counsellor: {a.counsellor_name || "Any"} · Booked by {a.created_by_name || "—"}{a.lead_stage ? ` · Lead stage: ${a.lead_stage}` : ""}</div>
                {a.note && <div className="text-xs text-slate-400 mt-0.5">{a.note}</div>}
                {a.status === "attended" && a.outcome && <div className="text-xs text-emerald-600 mt-0.5" data-testid={`appt-outcome-${a.id}`}>Outcome: {OUTCOME_LABEL[a.outcome] || a.outcome}</div>}
                {a.session_notes && <div className="text-xs text-slate-500 mt-0.5">Notes: {a.session_notes}</div>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${STATUS_TONE[a.status]}`}>{a.status}</span>
                {a.status === "booked" && <>
                  {(user.role === "admin" || user.role === "reception") && <Button size="sm" variant="outline" className="gap-1" onClick={() => openOutcome(a, "attended")} data-testid={`appt-checkin-${a.id}`}><UserCheck className="w-4 h-4" /> Check-in</Button>}
                  <Button size="sm" variant="outline" onClick={() => openOutcome(a, "attended")} data-testid={`appt-attended-${a.id}`}>Attended</Button>
                  <Button size="sm" variant="ghost" onClick={() => openOutcome(a, "no_show")} data-testid={`appt-noshow-${a.id}`}>No-show</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => window.confirm("Cancel this appointment? The lead moves back to Contacting.") && update.mutate({ id: a.id, body: { status: "cancelled" } })} data-testid={`appt-cancel-${a.id}`}>Cancel</Button>
                </>}
              </div>
            </div>
          ))}
        </div>
      )}

      {oc && (
        <Dialog open onOpenChange={() => setOc(null)}>
          <DialogContent data-testid="appt-outcome-dialog">
            <DialogHeader><DialogTitle>{oc.type === "attended" ? "Demo / counselling attended" : "Mark as no-show"} — {oc.appt.lead_name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {oc.type === "attended" ? (
                <div>
                  <Label>What happened in the session? *</Label>
                  <Select value={ocForm.outcome} onValueChange={(v) => setOcForm((f) => ({ ...f, outcome: v }))}>
                    <SelectTrigger data-testid="oc-outcome"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="follow_up">Need follow-up (lead stays Attended + reminder)</SelectItem>
                      <SelectItem value="admission_done">Admission done (lead → Admission Pending)</SelectItem>
                      <SelectItem value="not_interested">Not interested (lead → Lost)</SelectItem>
                    </SelectContent>
                  </Select>
                  {ocForm.outcome === "follow_up" && (
                    <div className="mt-3">
                      <Label>Schedule follow-up call {oc.appt.lead_id ? "" : "(needs a linked lead)"}</Label>
                      <Input type="datetime-local" data-testid="oc-followup" disabled={!oc.appt.lead_id} value={ocForm.followup_at} onChange={(e) => setOcForm((f) => ({ ...f, followup_at: e.target.value }))} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label>Next step</Label>
                    <Select value={ocForm.next_step || "none"} onValueChange={(v) => setOcForm((f) => ({ ...f, next_step: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid="oc-next-step"><SelectValue placeholder="Select next step" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not decided</SelectItem>
                        <SelectItem value="reschedule">Reschedule demo</SelectItem>
                        <SelectItem value="follow_up">Follow-up call</SelectItem>
                        <SelectItem value="drop">Drop</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reschedule demo to (optional)</Label>
                    <Input type="datetime-local" data-testid="oc-reschedule" value={ocForm.reschedule_at} onChange={(e) => setOcForm((f) => ({ ...f, reschedule_at: e.target.value }))} />
                    <p className="text-[11px] text-slate-400 mt-1">Creates a fresh demo appointment at the new time.</p>
                  </div>
                  <div>
                    <Label>Schedule follow-up call (optional) {oc.appt.lead_id ? "" : "— needs a linked lead"}</Label>
                    <Input type="datetime-local" data-testid="oc-followup" disabled={!oc.appt.lead_id} value={ocForm.followup_at} onChange={(e) => setOcForm((f) => ({ ...f, followup_at: e.target.value }))} />
                  </div>
                </div>
              )}
              <div>
                <Label>Notes</Label>
                <Textarea data-testid="oc-notes" placeholder={oc.type === "attended" ? "What was discussed, interest level, objections…" : "Why no-show, what was tried…"} value={ocForm.session_notes} onChange={(e) => setOcForm((f) => ({ ...f, session_notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button className="bg-brand-blue hover:bg-blue-700" disabled={update.isPending || (oc.type === "attended" && !ocForm.outcome)} onClick={submitOutcome} data-testid="oc-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {openLead && <LeadDrawer leadId={openLead} config={leadConfig} onClose={() => setOpenLead(null)} onChanged={() => qc.invalidateQueries()} />}
    </div>
  );
}
